import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CalendarCheck, CalendarClock, CalendarOff, Camera, ExternalLink,
  ImageIcon, Loader2, MapPin, Pencil, ShieldCheck, Trash2, Upload, X,
} from "lucide-react";

import { EventFormDialog } from "@/components/EventFormDialog";
import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useEvent } from "@/hooks/useDetail";
import {
  useDeleteEvent, useDeleteEventMemory, useUploadEventMemories,
} from "@/hooks/useEventMutations";
import { formatDateTime } from "@/lib/format";
import type { EventDetail as EventType, EventMemoryRow } from "@/lib/types";
import { cn } from "@/lib/utils";

import { MetaRow, NotFound } from "./DocumentDetail";

/** /events/:id — hero, date chip, full description, registration CTA. */
export default function EventDetail() {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id ? params.id : undefined;
  const { data: user } = useCurrentUser();
  const { data: event, isLoading, error } = useEvent(id);
  const del     = useDeleteEvent();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);

  if (isLoading) return <EventSkeleton />;
  if (error || !event) return <NotFound back="/events" label="events" />;

  const canManage = !!user && user.is_director_level;

  async function onDelete() {
    if (!event) return;
    const ok = await confirm({
      title: `Delete "${event.title}"?`,
      description: "This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(event.id, { onSuccess: () => navigate("/events") });
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <EventFormDialog open={editing} onOpenChange={setEditing} event={event} />

      {/* ---------- Back link + actions ---------- */}
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="-ml-3">
          <Link to="/events">
            <ArrowLeft className="mr-2 h-4 w-4" />Back to events
          </Link>
        </Button>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="mr-2 h-4 w-4" />Edit
            </Button>
            <Button
              variant="outline" size="sm"
              className="border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:bg-rose-500/15 hover:text-rose-800 dark:text-rose-300"
              disabled={del.isPending}
              onClick={onDelete}
            >
              {del.isPending
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Trash2 className="mr-2 h-4 w-4" />}
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* ---------- Hero ---------- */}
      <Card className="overflow-hidden border-border/60 shadow-soft">
        <div className="relative h-56 w-full bg-gradient-to-br from-emerald-100 to-emerald-50 sm:h-64">
          {event.image_url ? (
            <img
              src={event.image_url} alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <CalendarClock className="h-16 w-16 text-emerald-700 dark:text-emerald-300/40" />
            </div>
          )}
          <div className="absolute left-5 top-5 rounded-xl bg-card/95 px-4 py-2.5 text-center shadow-soft backdrop-blur">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              {new Date(event.start_at).toLocaleDateString(undefined, { month: "short" })}
            </div>
            <div className="font-mono text-2xl font-bold leading-none tabular-nums">
              {new Date(event.start_at).getDate()}
            </div>
          </div>
          <div className="absolute right-5 top-5">
            <StatusBadge event={event} />
          </div>
        </div>

        <CardContent className="space-y-3 p-6 md:p-8">
          <Badge variant="outline" className="bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30">
            {event.event_type_display}
          </Badge>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{event.title}</h1>
          {event.summary && (
            <p className="text-sm text-muted-foreground">{event.summary}</p>
          )}
        </CardContent>
      </Card>

      {/* ---------- Body + sidebar ---------- */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {event.description && (
            <Card className="border-border/60 shadow-soft">
              <CardHeader>
                <CardTitle className="text-base">Programme</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none whitespace-pre-wrap pt-0">
                {event.description}
              </CardContent>
            </Card>
          )}

          {event.registration_url && (
            <Card className="border-primary/30 bg-primary/5 shadow-soft">
              <CardContent className="flex flex-col items-start gap-3 p-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="font-semibold">Reserve your seat</div>
                  <div className="text-xs text-muted-foreground">
                    Registration link below opens externally.
                  </div>
                </div>
                <Button asChild>
                  <a href={event.registration_url} target="_blank" rel="noreferrer">
                    Register
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Post-event photo gallery. Always rendered for Director+ so
              they can start uploading even before the event ends; regular
              users only see it once at least one memory exists. */}
          <MemoriesCard event={event} canManage={!!user?.is_director_level} />
        </div>

        <Card className="h-fit border-border/60 shadow-soft">
          <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
          <CardContent className="space-y-3 pt-0 text-sm">
            <MetaRow label="Type"   value={event.event_type_display} />
            <MetaRow label="Starts" value={formatDateTime(event.start_at)} />
            {event.end_at && <MetaRow label="Ends" value={formatDateTime(event.end_at)} />}
            <MetaRow
              label="Location"
              value={event.location || "—"}
              icon={event.location ? <MapPin className="h-3 w-3" /> : undefined}
            />
            <MetaRow label="Host"    value={event.owner_name || "—"} />
            <MetaRow label="Created" value={formatDateTime(event.created_at)} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatusBadge({ event }: { event: EventType }) {
  if (event.needs_ceo_approval) {
    return (
      <Badge variant="outline" className={cn("border bg-violet-50 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-500/30 shadow-soft")}>
        <ShieldCheck className="mr-1 h-3 w-3" />Pending CEO
      </Badge>
    );
  }
  if (!event.is_published) {
    return (
      <Badge variant="outline" className="border bg-muted/40 text-foreground/80 border-border shadow-soft">
        Draft
      </Badge>
    );
  }
  if (event.is_past) {
    return (
      <Badge variant="outline" className="border bg-muted text-foreground/80 border-border shadow-soft">
        <CalendarOff className="mr-1 h-3 w-3" />Past
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30 shadow-soft">
      <CalendarCheck className="mr-1 h-3 w-3" />Upcoming
    </Badge>
  );
}

function EventSkeleton() {
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-64 w-full" />
      <div className="grid gap-6 lg:grid-cols-3">
        <Skeleton className="h-64 w-full lg:col-span-2" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post-event memory gallery
//
// Optional grid of photos taken AT the event. Director+ sees an "Add
// photos" panel even before there are any rows (so they can start the
// gallery the day-of). Regular users see the card only once something's
// been uploaded.
// ---------------------------------------------------------------------------
function MemoriesCard({
  event, canManage,
}: {
  event: EventType;
  canManage: boolean;
}) {
  const memories  = event.memories ?? [];
  const upload    = useUploadEventMemories();
  const removeOne = useDeleteEventMemory();
  const confirm   = useConfirm();

  const [picked,   setPicked]   = useState<File[]>([]);
  const [caption,  setCaption]  = useState("");
  const [previewId, setPreviewId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Regular users: no rows yet → don't render the card at all (keeps the
  // page clean before there's any content to show).
  if (!canManage && memories.length === 0) return null;

  function onPick(files: FileList | File[] | null) {
    if (!files) return;
    setPicked((prev) => [...prev, ...Array.from(files)]);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!picked.length || upload.isPending) return;
    upload.mutate(
      { eventId: event.id, files: picked, caption: caption.trim() || undefined },
      {
        onSuccess: () => {
          setPicked([]);
          setCaption("");
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
      },
    );
  }

  async function onRemove(m: EventMemoryRow) {
    const ok = await confirm({
      title: "Remove this photo?",
      description: "It will be deleted from the gallery permanently.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    removeOne.mutate({ eventId: event.id, memoryId: m.id });
  }

  const previewing = previewId
    ? memories.find((m) => m.id === previewId)
    : null;

  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 border-b border-border/60 py-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Camera className="h-4 w-4 text-muted-foreground" />
            Memories
            {memories.length > 0 && (
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                {memories.length} photo{memories.length === 1 ? "" : "s"}
              </span>
            )}
          </CardTitle>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Photos from the day — drop in any that capture the moment.
          </p>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 p-5">
        {/* Gallery — masonry-ish responsive grid. */}
        {memories.length > 0 ? (
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {memories.map((m) => (
              <li key={m.id} className="group relative aspect-square overflow-hidden rounded-md border border-border bg-muted/30">
                <button
                  type="button"
                  onClick={() => setPreviewId(m.id)}
                  className="block h-full w-full"
                  aria-label={m.caption || "Open photo"}
                >
                  {m.image_url ? (
                    <img
                      src={m.image_url}
                      alt={m.caption}
                      loading="lazy"
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground">
                      <ImageIcon className="h-6 w-6" />
                    </div>
                  )}
                  {m.caption && (
                    <span className="absolute inset-x-0 bottom-0 block bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-3 text-left text-[11px] text-white line-clamp-2">
                      {m.caption}
                    </span>
                  )}
                </button>
                {canManage && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemove(m); }}
                    aria-label="Remove photo"
                    className="absolute right-1.5 top-1.5 hidden h-7 w-7 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition group-hover:flex group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-md border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
            No memories yet. {canManage ? "Add the first photo below." : ""}
          </div>
        )}

        {/* Upload panel — director+ only. */}
        {canManage && (
          <form
            onSubmit={onSubmit}
            className="space-y-2 rounded-md border bg-muted/20 p-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={upload.isPending}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="mr-2 h-3.5 w-3.5" />Pick photos
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => { onPick(e.target.files); e.target.value = ""; }}
              />
              <Input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Optional caption applied to this batch"
                disabled={upload.isPending}
                maxLength={240}
                className="h-9 flex-1 min-w-[12rem]"
              />
              <Button type="submit" size="sm" disabled={!picked.length || upload.isPending}>
                {upload.isPending
                  ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Uploading…</>
                  : <><Camera className="mr-2 h-3.5 w-3.5" />
                      Add {picked.length > 0 ? `(${picked.length})` : ""}</>}
              </Button>
            </div>
            {picked.length > 0 && (
              <ul className="flex flex-wrap gap-1.5 pt-1">
                {picked.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2 py-0.5 text-[11px]"
                  >
                    <ImageIcon className="h-3 w-3 text-muted-foreground" />
                    <span className="max-w-[10rem] truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setPicked((prev) => prev.filter((_, j) => j !== i))}
                      className="rounded-full p-0.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
                      aria-label={`Remove ${f.name} from this batch`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </form>
        )}
      </CardContent>

      {/* Simple lightbox — clicking any thumbnail opens this. */}
      {previewing?.image_url && (
        <button
          type="button"
          onClick={() => setPreviewId(null)}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/85 p-4 backdrop-blur"
        >
          <img
            src={previewing.image_url}
            alt={previewing.caption}
            className="max-h-[85vh] max-w-full rounded-md object-contain shadow-soft-lg"
            onClick={(e) => e.stopPropagation()}
          />
          {previewing.caption && (
            <span className="mt-3 max-w-xl text-center text-sm text-white/90">
              {previewing.caption}
            </span>
          )}
          <span className="mt-2 text-xs text-white/60">Click anywhere to close</span>
        </button>
      )}
    </Card>
  );
}
